import { Component, ViewChild, Inject, DOCUMENT, ChangeDetectionStrategy, ChangeDetectorRef, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FormControl, FormsModule } from "@angular/forms";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { MatPaginator } from "@angular/material/paginator";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ValidationService } from "../service/validation.service";
import { Pair } from "../obj/Pair";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { State } from "../entity/State";
import { Account } from "../entity/Account";
import { Contract } from "../entity/Contract";
import { KilowantContract } from "../entity/KilowantContract";
import { BuyOffer } from "../entity/BuyOffer";
import { MatSort, MatSortHeader } from "@angular/material/sort";
import { merge, of as observableOf } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { timer } from "rxjs";
import { AccountNumberIssueDialog } from "./incs/account-number-issue-dialog.component";
import { HeaderComponent } from "./incs/header.component";
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from "@angular/material/table";
import { AccountEditComponent } from "./incs/account-edit.component";
import { AccountConsumptionComponent } from "./incs/account-consumption.component";
import { DatePipe } from "@angular/common";
import { Company } from "../entity/Company";

@Component({
    selector: "kilowant-account-list",
    templateUrl: "../../html/main-account-list.html",
    imports: [HeaderComponent, FormsModule, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, AccountEditComponent, AccountConsumptionComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountListComponent extends AbstractBuyerComponent {
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    document: Document;

    accountListSubscription: Subscription | null;
    selectedAccount: Pair | null = null;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    fragment: string | null;
    states: State[] = [];
    accounts: Pair[] = null!;
    viewContracts: boolean[] = [];
    viewOldAccountNumbers: boolean[] = [];
    viewBuyOffers: boolean[] = [];
    viewConsumption: boolean[] = [];
    viewDocuments: boolean[] = [];
    oldAccountNumbers: string[] = [];
    currentStart: number = 0;
    currentAccountNumber: FormControl = new FormControl("");
    selectedAccountAssociatedContracts: Contract[] = [];
    selectedAccountBuyOffers: BuyOffer[] = [];
    editModePos: number = -1;
    editModeBlocks: boolean[] = [];
    filesToUpload: File[] = [];
    filesToDelete: number[] = [];
    accountActivitiesSubscription: Subscription;
    clientActivitiesSubscription: Subscription;
    countDown: Subscription;

    displayedColumns: string[] = ["id", "ServiceAddress", "AccountNumber", "RateClass"];
    total: number = 0;
    viewActiveType: string = "all";
    keywords: string | null = null;

    companyFilter = signal<boolean>(false);
    selectedFilterCompany: Company | string | null = "all";
    userCompanies: (Company|null)[];

    callUpdate: Subject<boolean> = new Subject();
    callCancel: Subject<boolean> = new Subject();

    loaded: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog,
        @Inject(DOCUMENT) document: Document
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
        this.document = document;
    }

    ngOnInit(): void {
        this.analyticsService.trackEvent("Accounts Listing Page", "User Opens Accounts Listing Page", "OPEN_PAGE");
        super.ngOnInit();
    }

    pageStart(): void {
        this.route.fragment.subscribe((fragment: string | null) => {
            this.fragment = fragment;
        });
        this.refreshFilter();
        this.clientActivitiesSubscription = this.generalService.changeClientActivities$.subscribe(
            client => {
                if (client != null) {
                    this.refreshAccounts(true);
                }
            });
    }

    refreshFilter(): void {
        const userFilter = this.generalService.getStoredUserFilter();
        if (userFilter && !this.user.isAdvisor) {
            this.companyFilter.set(true);
            firstValueFrom(this.generalService.getUsersActiveCompanies()).then(companies => {
                this.userCompanies = companies;
                this.refreshAccounts(true);
            });
        } else {
            this.refreshAccounts(true);
        }
    }

    refreshAccounts(selectDefault: boolean): void {
        if (this.accountListSubscription != null) {
            this.accountListSubscription.unsubscribe();
            this.accountListSubscription = null;
        }
        this.loaded = false;
        this.total = 0;
        this.refreshView();

        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.accountListSubscription = merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    let viewType = null;
                    if (this.viewActiveType == "active") {
                        viewType = true;
                    }
                    if (this.viewActiveType == "inactive") {
                        viewType = false;
                    }
                    return this.generalService.getAccountsPlusUsed(viewType, (this.selectedFilterCompany != "all" && this.companyFilter()), <Company | null>this.selectedFilterCompany, this.keywords, this.total, this.paginator.pageIndex, this.paginator.pageSize, this.sort.active, (this.sort.direction == "desc"));
                }),
                map(data => {
                    if (data === null) {
                        return [];
                    }
                    if (data.key != 0 && this.total != data.key) {
                        this.total = data.key;
                    }
                    this.currentStart = this.paginator.pageIndex * this.paginator.pageSize;
                    return data.value;
                }),
            )
            .subscribe(data => {
                this.accounts = data;
                this.viewContracts = [];
                this.viewBuyOffers = [];
                this.viewConsumption = [];
                this.viewOldAccountNumbers = [];
                this.viewDocuments = [];
                for (var i = 0; i < this.accounts.length; i++) {
                    this.viewContracts.push(false);
                    this.viewBuyOffers.push(false);
                    this.viewConsumption.push(false);
                    this.viewOldAccountNumbers.push(false);
                    this.viewDocuments.push(false);
                }
                if (selectDefault && this.fragment != null && this.fragment.length > 0) {
                    let pos = -1;
                    for (let i = 0; i < this.accounts.length; i++) {
                        if (this.accounts[i].key.id == this.fragment) {
                            pos = i;
                        }
                    }
                    if (pos >= 0) {
                        this.editAccount(pos, true);
                        return;
                    }
                }
                this.resetSelectedAccount();
                this.loaded = true;
                this.cdr.detectChanges();
            });
    }

    searchSiteInputChanged(event: any): void {
        if (this.countDown) {
            this.countDown.unsubscribe();
        }
        this.countDown = timer(500).subscribe((count) => {
            this.total = 0;
            this.paginator.pageIndex = 0;
            this.refreshAccounts(true);
        });
    }

    addAccount(): void {
        this.router.navigate(["/account/add"]);
    }

    editAccount(pos: number, openConsumption: boolean): void {
        if (this.editModePos == pos) {
            this.resetSelectedAccount();
        } else {
            this.selectedAccount = new Pair(this.accounts[pos].key, this.accounts[pos].value);
            this.generalService.getContracts(this.accounts[pos].key, false, null, true).then((es) => {
                this.selectedAccountAssociatedContracts = es;
                this.generalService.getBuyOffersPerAccount(this.accounts[pos].key, null, null, null, 0, 0, 100, "Price", false).then(bo => this.selectedAccountBuyOffers = bo.value);
            })
                .then(() => {
                    this.editModePos = pos;
                    this.editModeBlocks = [false, false];
                    if (openConsumption) {
                        this.viewConsumption[pos] = true;
                        this.eventSubscriptions.push(timer(300).subscribe((count) => {
                            if (this.document.getElementById('account-consumption-' + pos) != null) {
                                let num = this.document.getElementById('account-consumption-' + pos)!.getBoundingClientRect().top - this.generalService.getHeaderHeight() - 10;
                                this.generalService.scrollPageBy(num);
                            }
                        }));
                    }
                    this.cdr.detectChanges();
                });
            this.generalService.getAccountOldNumbers(this.accounts[pos].key).then((data) => { this.oldAccountNumbers = data; this.cdr.detectChanges(); });
        }
        this.clearErrors();
    }

    deleteAccount(account: Account): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this account?"

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.deleteAccount(account.id)
                    .subscribe((res: any) => {
                        this.generalService.clearUserCompaniesFromSession();
                        if (res) {
                            this.refreshAccounts(true);
                        }
                    },
                        err => {
                        });
            }
            this.dialogRef = null!;
            this.editModePos = -1;
            this.editModeBlocks = [];
            this.cdr.detectChanges();
        });
    }

    deleteFile(accountInfo: Account | null, pos: number, ifExisting: boolean) {
        if (!ifExisting) {
            this.filesToUpload.splice(pos, 1);
        } else if (accountInfo != null) {
            this.filesToDelete.push(accountInfo.billImages[pos].id);
            accountInfo.billImages.splice(pos, 1);
        }
        this.cdr.markForCheck();
    }

    downloadFile(accountInfo: Account | null, pos: number, ifExisting: boolean) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.filesToUpload[pos]);
        } else if (accountInfo != null) {
            this.generalService.openDocument(accountInfo.billImages[pos].id);
        }
    }

    downloadFileFromView(id: number) {
        this.generalService.openDocument(id);
    }

    cancel(): void {
        this.callCancel.next(true);
    }

    updateAccount(): void {
        this.callUpdate.next(true);
    }

    editFinish(num: number): void {
        this.editModeBlocks[num] = true;
        for (let i = 0; i < this.editModeBlocks.length; i++) {
            if (!this.editModeBlocks[i]) {
                return;
            }
        }
        this.refreshFilter();
    }

    editCancel(): void {
        this.resetSelectedAccount();
        this.cdr.detectChanges();
    }

    resetSelectedAccount(): void {
        this.selectedAccount = null;
        this.selectedAccountAssociatedContracts = [];
        this.selectedAccountBuyOffers = [];
        this.editModePos = -1;
        this.editModeBlocks = [];
        this.clearErrors();
        this.editModePos = -1;
        this.editModeBlocks = [];
        this.filesToUpload = [];
        this.filesToDelete = [];
        this.clearErrors();
        this.cdr.markForCheck();
    }

    ngOnDestroy() {
        if (this.accountActivitiesSubscription != null) {
            this.accountActivitiesSubscription.unsubscribe();
        }
        if (this.clientActivitiesSubscription != null) {
            this.clientActivitiesSubscription.unsubscribe();
        }
        if (this.accountListSubscription != null) {
            this.accountListSubscription.unsubscribe();
        }
        super.ngOnDestroy();
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.filesToUpload.push((target.files as FileList).item(0) as File);
        this.resetError("fileToUpload");
        this.cdr.markForCheck();
    }

    getClass(associatedContract: Contract, pos: number): string {
        return ((associatedContract instanceof KilowantContract) ? "bolt-li" : "") + " " + ((this.viewContracts[pos]) ? "visible" : "hidden");
    }

    getPrice(associatedContract: Contract): number {
        return associatedContract.getSalesPrice()
    }

    goto(url: string): void {
        this.generalService.closeSidenav();
        this.router.navigate([url]);
    }

    reportAccountIssue(): void {
        var dialogRef = this.dialog.open(AccountNumberIssueDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        dialogRef.componentInstance.info = "Please, provide more details about your account (optional).";
        dialogRef.componentInstance.message = "My Account Number: " + this.currentAccountNumber.value + " has been used without my authorization. Please, investigate this.";
        dialogRef.componentInstance.utility = this.selectedAccount!.key.utility;
        dialogRef.componentInstance.accountNumber = this.currentAccountNumber.value;

        dialogRef.afterClosed().subscribe((result: any) => {
            dialogRef = null!;
        });
    }

    getNameForAll(): string {
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                let res = "All " + client.firstName + " " + client.lastName;
                if (client.company != null) {
                    res += " (" + client.company.name + ")";
                }
                res += "'s accounts";
                return res;
            }
        }
        return "All accounts";
    }

    getUserCompanyName(company: Company | null): string {
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                return (company == null) ? (client.firstName + " " + client.lastName) : company.name;
            }
        }
        return (company == null) ? (this.user.firstName + " " + this.user.lastName) : company.name;
    }
}
